import { useTranslation, t } from '../../../shared/i18n';
import React, { useEffect, useState, useCallback } from 'react';
import { Drawer, Table, Tag, Button, Space, Typography, Alert, Spin, message, Tooltip } from 'antd';
import { CopyOutlined, ReloadOutlined, AppleOutlined, MailOutlined, AndroidOutlined } from '@ant-design/icons';
import webmailService from '../../services/webmailService';
import DnsOneClick from '../../components/DnsOneClick';

const { Text, Paragraph } = Typography;

const copy = async (text) => {
    try {
        await navigator.clipboard.writeText(String(text));
        message.success(t('Copied'));
    } catch {
        // Fallback for non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = String(text);
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); message.success(t('Copied')); } catch { message.error(t('Failed to copy. Please select the content and copy it manually.')); }
        document.body.removeChild(ta);
    }
};

const Copyable = ({ value, code = true }) => {
    const { t } = useTranslation();
    return (
    <Space size={4} style={{ maxWidth: '100%' }}>
        {code ? <Text code style={{ wordBreak: 'break-all' }}>{value}</Text> : <Text style={{ wordBreak: 'break-all' }}>{value}</Text>}
        <Tooltip title={t("Copy")}>
            <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => copy(value)} />
        </Tooltip>
    </Space>
    );
};

const ClientSetupDrawer = ({ open, domain, onClose }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

    useEffect(() => {
        const onResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const load = useCallback(async () => {
        if (!domain) return;
        setLoading(true);
        setError(null);
        const res = await webmailService.getClientSetup(domain.local_id);
        if (res && (res.code === 0 || res.success)) {
            setData(res.data);
        } else {
            setError(res?.error || res?.message || t('Failed to load'));
        }
        setLoading(false);
    }, [domain, t]);

    useEffect(() => {
        if (open) load();
        else setData(null);
    }, [open, load]);

    const isMobile = width < 640;
    const d = data?.domain || domain?.name || '';
    const rows = data ? [
        { key: 'imap', proto: 'IMAP', ...data.imap },
        { key: 'imap143', proto: 'IMAP', ...data.imap_starttls },
        { key: 'smtp', proto: 'SMTP', ...data.smtp },
        { key: 'sub', proto: 'SMTP', ...data.submission },
        { key: 'pop3', proto: 'POP3', ...data.pop3 },
    ] : [];

    return (
        <Drawer
            title={<Space><MailOutlined />{t('Client settings · {{domain}}', { domain: d })}</Space>}
            open={open}
            onClose={onClose}
            width={isMobile ? '100%' : Math.min(860, width - 80)}
            extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>{t("Refresh")}</Button>}
            destroyOnClose
        >
            {loading && !data && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
            {data && (
                <>
                    <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                        {t("Username: full email address · Password: mailbox password (use an app password if two-step verification is enabled)")}
                    </Paragraph>

                    <Typography.Title level={5} style={{ marginTop: 0 }}>{t("Server")}</Typography.Title>
                    {isMobile ? (
                        <Space direction="vertical" style={{ width: '100%' }} size={2}>
                            {rows.map(r => (
                                <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <Text strong style={{ width: 44 }}>{r.proto}</Text>
                                    <Copyable value={`${r.host}:${r.port}`} />
                                    <Tag color={r.security === 'STARTTLS' ? 'blue' : 'green'} style={{ marginInlineEnd: 0 }}>{r.security}</Tag>
                                </div>
                            ))}
                        </Space>
                    ) : (
                        <Table
                            size="small"
                            pagination={false}
                            rowKey="key"
                            dataSource={rows}
                            columns={[
                                { title: t('Protocol'), dataIndex: 'proto' },
                                { title: t('Server'), dataIndex: 'host', render: v => <Copyable value={v} /> },
                                { title: t('Port'), dataIndex: 'port', width: 110, render: v => <Copyable value={v} /> },
                                { title: t('Encryption'), dataIndex: 'security', width: 110, render: v => <Tag color={v === 'STARTTLS' ? 'blue' : 'green'}>{v}</Tag> },
                            ]}
                        />
                    )}
                    <div style={{ marginTop: 8 }}>
                        <Text type="secondary">{t("Webmail:")}</Text><Copyable value={data.webmail_url} />
                    </div>

                    <Alert
                        type="info" showIcon icon={<AppleOutlined />} style={{ marginTop: 12 }}
                        message={<span>{t("iPhone/Mac profiles and Android QR codes: sign in to Webmail → Settings → Client settings")}</span>}
                    />

                    <Button icon={<AndroidOutlined />} href="https://api.yeap.cloud/api/public/apps/yeap-mail.apk" style={{ marginTop: 12 }}>{t("Download Android app")}</Button>

                    {domain?.local_id && <div style={{ marginTop: 20 }}>
                        <DnsOneClick
                            app="webmail_client"
                            id={domain.local_id}
                            title={t('Auto-detection DNS records (optional)')}
                        />
                    </div>}
                </>
            )}
        </Drawer>
    );
};

export default ClientSetupDrawer;
